import { useState, useCallback } from "react";
import { message } from "antd";
import { useTranslation } from "react-i18next";
import {
    getTeacherJournals,
    createTeacherJournal,
    updateTeacherJournal,
    deleteTeacherJournal,
} from "services/teacherJournalService";
import { TeacherJournal, TeacherJournalCreatePayload, TeacherJournalUpdatePayload } from "types/teacherJournalTypes";


export const useTeacherJournal = () => {
    const { t } = useTranslation();

    const [journals, setJournals] = useState<TeacherJournal[]>([]);
    const [pagination, setPagination] = useState<{
        page: number;
        limit: number;
        total: number;
    }>({
        page: 1,
        limit: 10,
        total: 0,
    });
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    const [selectedJournal, setSelectedJournal] = useState<TeacherJournal | null>(
        null
    );


    const fetchTeacherJournals = useCallback(
        async (params: { [key: string]: string | number | null } = {}) => {
            setLoading(true);
            try {
                const response = await getTeacherJournals(params);
                setJournals(response.data || []);
                setPagination(response.pagination || { page: 1, limit: 10, total: 0 });
                setError(null);
            } catch (err) {
                console.error(err);
                setJournals([]);
                const errorMsg = t("errors.ERROR_FETCHING_DATA") as string;
                message.error(errorMsg);
                setError(errorMsg);
            } finally {
                setLoading(false);
            }
        },
        [t]
    );


    const handleCreate = async (payload: TeacherJournalCreatePayload) => {
        try {
            await createTeacherJournal(payload);
            message.success(t("teacherJournal.succes_save"));
            return true;
        } catch (err) {
            console.error(err);
            return false;
        }
    };


    const handleUpdate = async (
        journalId: string,
        payload: TeacherJournalUpdatePayload
    ) => {
        try {
            await updateTeacherJournal(journalId, payload);
            message.success(t("teacherJournal.succes_update"));
            return true;
        } catch (err) {
            console.error(err);
            return false;
        }
    };


    const handleDelete = async (journalId: string) => {
        try {
            await deleteTeacherJournal(journalId);
            message.success(t("teacherJournal.succes_delete"));
            return true;
        } catch (err) {
            console.error(err);
            return false;
        }
    };

    return {
        journals,
        infoPagination: {
            total: pagination.total,
            page: pagination.page,
            limit: pagination.limit,
        },
        pagination,
        loading,
        error,
        selectedJournal,
        setSelectedJournal,
        fetchTeacherJournals,
        handleCreate,
        handleUpdate,
        handleDelete,
    };
};

export default useTeacherJournal;
